// "Leitura da IA" da Meta de Frentista (aba do quadro e sugestão do simulador). O visual é do AiInsightPanel;
// aqui ficam o polling (`useInsightPolling`, também usado pelo convite) e o texto fixo por estado.
import React, { useEffect, useRef, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { useLocaleState } from 'react-admin';
import { __t } from '../../i18n/t';
import { InsightDecisions, InsightResponse, InsightStatus, InsightVerdict } from '../../models/attendantGoalOffer';
import AiInsightPanel from '../AiInsightPanel';

export type AiLocale = 'pt-br' | 'es';

const MAX_RETRIES = 4;
const DEFAULT_RETRY_SECONDS = 6;

/**
 * Carrega uma leitura e repete até MAX_RETRIES vezes enquanto o status for `generating`
 * (espera `retryAfterSeconds`). `resetKey` + idioma recarregam; `enabled=false` fica ocioso.
 * `load` e `getInsight` entram por ref: podem mudar a cada render sem disparar nova busca.
 */
export function useInsightPolling<T>(
  load: (locale: AiLocale) => Promise<T | null>,
  getInsight: (value: T) => InsightResponse | undefined,
  resetKey: string,
  enabled = true,
): { loading: boolean; data: T | null } {
  const [localeState] = useLocaleState();
  const locale: AiLocale = localeState === 'es' ? 'es' : 'pt-br';
  const key = `${resetKey}|${locale}`;
  const loadRef = useRef(load);
  loadRef.current = load;
  const insightRef = useRef(getInsight);
  insightRef.current = getInsight;
  const [state, setState] = useState<{ key: string; data: T | null }>({ key: '', data: null });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const run = async (attempt: number) => {
      try {
        const value = await loadRef.current(locale);
        if (cancelled) return;
        const insight = value === null ? undefined : insightRef.current(value);
        if (insight?.status === InsightStatus.generating && attempt < MAX_RETRIES) {
          timer = setTimeout(() => run(attempt + 1), (insight.retryAfterSeconds ?? DEFAULT_RETRY_SECONDS) * 1000);
          return;
        }
        setState({ key, data: value ?? null });
      } catch {
        if (!cancelled) setState({ key, data: null });
      }
    };
    run(0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [key, enabled]);

  const done = enabled && state.key === key;
  return { loading: enabled && !done, data: done ? state.data : null };
}

type ChipColor = 'info' | 'success' | 'warning' | 'default';

// Decisão do código que a IA só explica (spec 4.1).
const decisionChip = (decisions?: InsightDecisions): { label: string; color: ChipColor } | null => {
  switch (decisions?.verdict) {
    case InsightVerdict.positive: return { label: __t('Indicativo positivo'), color: 'success' };
    case InsightVerdict.neutral: return { label: __t('Indicativo neutro'), color: 'info' };
    case InsightVerdict.negative: return { label: __t('Indicativo de queda'), color: 'warning' };
    case InsightVerdict.noData: return { label: __t('Sem dados suficientes'), color: 'default' };
    default: break;
  }
  switch (decisions?.recommendation) {
    case 'wait': return { label: __t('Ainda cedo para mexer na meta'), color: 'info' };
    case 'raise': return { label: __t('Dá para subir a meta'), color: 'success' };
    case 'lower': return { label: __t('Vale reduzir a meta'), color: 'warning' };
    case 'keep': return { label: __t('Manter a meta'), color: 'success' };
    default: return null;
  }
};

/**
 * Só apresenta. `response` null = a chamada falhou (mesmo texto de `unavailable`).
 * `insufficientNode` troca o painel de texto fixo do `insufficient_data` por um bloco próprio (ex.: estado vazio do quadro).
 */
export const AttendantGoalAiInsightView: React.FC<{
  response: InsightResponse | null;
  insufficientText?: string;
  insufficientNode?: React.ReactNode;
  sx?: Record<string, any>;
}> = ({ response, insufficientText, insufficientNode, sx }) => {
  const content = response?.status === InsightStatus.ready ? response.content ?? null : null;
  if (!content && response?.status === InsightStatus.insufficientData && insufficientNode) {
    return <Box sx={sx}>{insufficientNode}</Box>;
  }
  let message: string | undefined;
  if (!content) {
    if (response?.status === InsightStatus.insufficientData) {
      message = insufficientText ?? __t('Ainda não há dados suficientes para a leitura da IA.');
    } else if (response?.status === InsightStatus.generating) {
      message = __t('A leitura da IA ainda está sendo preparada. Volte em alguns minutos.');
    } else {
      message = __t('A leitura da IA não está disponível agora. Os números da tela continuam valendo.');
    }
  }
  const chip = content ? decisionChip(response?.decisions) : null;
  return (
    <AiInsightPanel
      content={content}
      loading={false}
      message={message}
      sx={sx}
      chip={chip && <Chip size="small" label={chip.label} color={chip.color} />}
    />
  );
};

const AttendantGoalAiInsight: React.FC<{
  resetKey: string;
  load: (locale: AiLocale) => Promise<InsightResponse | null>;
  insufficientText?: string;
  insufficientNode?: React.ReactNode;
}> = ({ resetKey, load, insufficientText, insufficientNode }) => {
  const { loading, data } = useInsightPolling<InsightResponse>(load, (response) => response, resetKey);
  if (loading) return <AiInsightPanel content={null} loading sx={{ mt: 2 }} />;
  return <AttendantGoalAiInsightView response={data} insufficientText={insufficientText} insufficientNode={insufficientNode} sx={{ mt: 2 }} />;
};

export default AttendantGoalAiInsight;
